import {
  Accordion,
  Badge,
  CountriesInput,
  SelectBox,
  TextField,
  Typography,
} from "@/components/kit";
import OrgLogoFilePicker from "./OrgLogoFilePicker";
import { useStoreOrg } from "../../store/useStoreOrganization";
import SvgIcon from "@/components/common/SvgIcon";
import useBreadcrumbs from "@/hooks/store/useBreadcrumbs";
import { useEffect, useState } from "react";
import { industryOptions, regionalList } from "../../constants";
import classNames from "classnames";

const OrgDetailStep = () => {
  const { setState, country, region, industry, name, regionDomain } =
    useStoreOrg();
  const [isOpenAdminPersonalInformation, setIsOpenAdminPersonalInformation] =
    useState<number | undefined>(0);
  const { setRoutes } = useBreadcrumbs();

  useEffect(() => {
    setRoutes([
      {
        name: "Organizations",
        path: "/organizations",
      },
      {
        name: "Add",
        path: "/organizations/add",
        description:
          "Add a new organization to securely manage each client’s devices, users, and settings separately. This ensures clear boundaries, better visibility, and safer administration.",
        helpLink:
          "https://help.trio.so/en/articles/12522035-adding-a-new-organization-in-trio-msp",
      },
    ]);
    return () => {
      setRoutes([]);
    };
  }, [setRoutes]);
  return (
    <Accordion
      title={
        <div className="flex  flex-col gap-2">
          <div className="flex items-center gap-2">
            <Typography variant="Title4-Regular">
              🏢 Organization details
            </Typography>
            <Badge size="sm" state="danger">
              Required
            </Badge>
          </div>
          <Typography
            variant="Body2-Regular"
            className="!text-gray-300 !text-[13px]"
          >
            Enter key details to identify and customize your organization’s
            profile.
          </Typography>
        </div>
      }
      isOpen={isOpenAdminPersonalInformation === 0}
      onChange={() => {
        setIsOpenAdminPersonalInformation((p) => (p === 0 ? undefined : 0));
      }}
      descriptionClassName={classNames("!mt-0 !px-6", {
        "!py-0": isOpenAdminPersonalInformation !== 0,
        "!pb-4": isOpenAdminPersonalInformation === 0,
      })}
      headerClassName={classNames("!px-6 !py-5")}
    >
      <div className="space-y-4">
        <OrgLogoFilePicker />

        <CountriesInput
          value={country}
          onChange={(value) => setState("country", value.code)}
          placeholder="Set HQ location"
        />
        <SelectBox
          value={industry}
          placeholder={"Select your industry"}
          options={industryOptions}
          renderOption={(item) => (
            <Typography variant="Body1-Regular">{item}</Typography>
          )}
          valueKey="id"
          renderValue={(item) => (
            <Typography variant="Body1-Regular">{item}</Typography>
          )}
          onChange={(value) => {
            setState("industry", value);
          }}
        />
        <TextField
          onChange={(e) => {
            setState("name", e.target.value);
            setState(
              "region",
              e.target.value.replace(/\s+/g, "").toLowerCase(),
            );
          }}
          label={"Company name"}
          value={name}
        />
        <div>
          <div className="flex items-center border border-gray-200 rounded-[6px]">
            <TextField
              placeholder=""
              value={region}
              className=" !border-t-0 !border-l-0 !rounded-none !border-b-0 !border-r-2"
              disabled
            />
            <SelectBox
              className="!border-0"
              renderValue={(val) => (
                <div className="flex gap-2">
                  <div>{val.title}</div>
                </div>
              )}
              renderOption={(option) => (
                <div className="flex">
                  <div>{option.title}</div>
                </div>
              )}
              options={regionalList}
              value={regionDomain}
              onChange={(value) => setState("regionDomain", value)}
            />
          </div>
          <div className="flex items-center gap-1 mt-1">
            <SvgIcon
              className="p-0 h-4 w-4 !fill-blue-600 cursor-pointer"
              iconName="info"
            />
            <p className="text-gray-500 text-xs">
              The URL will be used to access your Trio account and cannot be
              changed. Please choose wisely.
            </p>
          </div>
        </div>
      </div>
    </Accordion>
  );
};

export default OrgDetailStep;
